/* iter103 — Parent replies to group messages (admin/director view). */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { ChatCircleDots, Check, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmtDate = (iso) => { const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); };

const SmsRepliesPanel = ({ canManage = true, onUnread }) => {
  const [items, setItems] = useState([]);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/sms/replies', { params: { unread: onlyUnread, limit: 200 } });
      setItems(data.items || []);
      onUnread?.(data.unread || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur chargement des réponses'); }
  }, [onlyUnread, onUnread]);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try { await api.post(`/sms/replies/${id}/read`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const answer = async (id) => {
    const text = (drafts[id] || '').trim();
    if (!text) return;
    setBusy(id);
    try {
      await api.post(`/sms/replies/${id}/answer`, { message: text });
      toast.success('Réponse envoyée au parent');
      setDrafts(d => ({ ...d, [id]: '' }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setBusy(''); }
  };

  return (
    <div className="space-y-2" data-testid="sms-replies-panel">
      <label className="flex items-center gap-2 text-xs text-[#64748B] cursor-pointer">
        <input type="checkbox" checked={onlyUnread} onChange={e => setOnlyUnread(e.target.checked)} data-testid="sms-replies-unread-only" />
        Non lues uniquement
      </label>
      {items.length === 0 && (
        <div className="text-center text-sm text-[#94A3B8] py-6" data-testid="sms-replies-empty">Aucune réponse de parent</div>
      )}
      <div className="space-y-2">
        {items.map(r => (
          <div key={r.id} data-testid={`sms-reply-${r.id}`}
            className={`rounded-md border p-3 ${r.read ? 'border-[#E2E8F0] bg-white' : 'border-indigo-200 bg-indigo-50/40'}`}>
            <div className="flex flex-wrap items-center gap-2 text-xs text-[#64748B]">
              <ChatCircleDots size={14} className="text-indigo-600" />
              <b className="text-[#0F172A]">{r.parent_name || 'Parent'}</b>
              {r.student_name && <span>· {r.student_name} {r.class_name ? `(${r.class_name})` : ''}</span>}
              <span>· {fmtDate(r.created_at)}</span>
              {!r.read && <span className="px-1.5 py-0.5 rounded bg-indigo-600 text-white text-[10px]">Nouveau</span>}
              {canManage && !r.read && (
                <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 text-xs" onClick={() => markRead(r.id)} data-testid={`sms-reply-read-${r.id}`}>
                  <Check size={12} className="mr-1" /> Marquer lu
                </Button>
              )}
            </div>
            <div className="mt-1 text-sm text-[#0F172A] whitespace-pre-wrap">{r.message}</div>
            {r.broadcast_message && (
              <div className="mt-1 text-[11px] text-[#94A3B8] truncate" title={r.broadcast_message}>
                En réponse à : « {r.broadcast_message} »
              </div>
            )}
            {(r.answers || []).length > 0 && (
              <div className="mt-2 space-y-1 border-l-2 border-emerald-300 pl-2" data-testid={`sms-reply-answers-${r.id}`}>
                {r.answers.map(a => (
                  <div key={a.id} className="text-xs text-[#334155]">
                    <span className="text-emerald-700 font-medium">{a.by_user_name || 'École'}</span>
                    <span className="text-[#94A3B8]"> · {fmtDate(a.created_at)} · </span>{a.message}
                  </div>
                ))}
              </div>
            )}
            {canManage && (
              <div className="mt-2 flex gap-2">
                <input value={drafts[r.id] || ''} onChange={e => setDrafts(d => ({ ...d, [r.id]: e.target.value.slice(0, 500) }))}
                  placeholder="Répondre au parent…" data-testid={`sms-reply-answer-input-${r.id}`}
                  className="flex-1 rounded-md border border-[#E2E8F0] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#0052FF]/40" />
                <Button size="sm" className="h-7 px-2 bg-[#0052FF] hover:bg-[#0041CC] text-white" onClick={() => answer(r.id)}
                  disabled={busy === r.id || !(drafts[r.id] || '').trim()} data-testid={`sms-reply-answer-send-${r.id}`}>
                  <PaperPlaneTilt size={12} />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SmsRepliesPanel;
